import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useQuery } from '@apollo/client';

import type { WorkflowBatchJobsQueryQuery } from '@/apollo/graphql';
import { WORKFLOW_BATCH_JOBS_QUERY } from '@/gql/queries/batch-action.gql';
import { isTerminalBatchJobStatus } from './useBatchJob';

export type BatchJobSummary = NonNullable<
  WorkflowBatchJobsQueryQuery['workflowBatchJobs']
>['jobs'][number];

export type BatchJobFilter = 'all' | 'running' | 'completed' | 'failures';

/** How the batch was selected. `all` sends no mode, so the server returns every kind. */
export type BatchModeFilter = 'all' | 'explicit' | 'filter' | 'migration';

const PAGE_SIZE = 25;
const NON_TERMINAL_STATUSES = ['PENDING', 'QUEUED', 'RUNNING'];
const COMPLETED_STATUSES = ['DONE', 'FAILED', 'CANCELLED'];

// The list is a summary, so it refreshes more slowly than the open job's own
// poll (2s in useBatchJob). 5s is plenty to watch counters climb.
const POLL_MS = 5000;
// A batch_job row only advances via Temporal activities, so a dead worker
// leaves it non-terminal forever — and "poll while anything looks unfinished"
// would then poll for the lifetime of the page. Give up after this many ticks
// with no observed change; any real movement resets the count.
const IDLE_TICKS_BEFORE_GIVING_UP = 10;

type FilterVariables = {
  statuses?: string[];
  onlyWithFailures?: boolean;
};

function filterVariables(filter: BatchJobFilter): FilterVariables {
  if (filter === 'running') {
    return { statuses: NON_TERMINAL_STATUSES };
  }
  if (filter === 'completed') {
    return { statuses: COMPLETED_STATUSES };
  }
  if (filter === 'failures') {
    return { onlyWithFailures: true };
  }
  return {};
}

export function useWorkflowBatchJobs(
  workflowId: string,
  options: {
    filter: BatchJobFilter;
    mode?: BatchModeFilter;
    allUsers: boolean;
    runBy?: string;
  }
) {
  const { filter, mode = 'all', allUsers, runBy } = options;

  const input = useMemo(
    () => ({
      workflowId,
      limit: PAGE_SIZE,
      allUsers,
      ...(runBy ? { runBy } : {}),
      ...(mode === 'all' ? {} : { modes: [mode] }),
      ...filterVariables(filter)
    }),
    [workflowId, allUsers, runBy, mode, filter]
  );

  const pagesAppendedRef = useRef(false);
  const inFlightRef = useRef(false);

  const { data, loading, fetchMore, refetch } = useQuery(
    WORKFLOW_BATCH_JOBS_QUERY,
    {
      variables: { input },
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      pollInterval: 0
    }
  );

  const jobs = useMemo(
    () => data?.workflowBatchJobs?.jobs ?? [],
    [data?.workflowBatchJobs?.jobs]
  );

  const hasNonTerminalJob = useMemo(
    () => jobs.some(job => !isTerminalBatchJobStatus(job.status)),
    [jobs]
  );

  // Progress fingerprint: changes whenever a job appears, changes status, or
  // advances. Unchanged across ticks means nothing is actually running.
  const progressSignature = useMemo(
    () => jobs.map(j => `${j.id}:${j.status}:${j.processed}`).join('|'),
    [jobs]
  );

  const [gaveUp, setGaveUp] = useState(false);
  const idleTicksRef = useRef(0);
  const lastSignatureRef = useRef(progressSignature);

  useEffect(() => {
    if (progressSignature === lastSignatureRef.current) {
      return;
    }
    lastSignatureRef.current = progressSignature;
    idleTicksRef.current = 0;
    setGaveUp(false);
  }, [progressSignature]);

  const shouldPoll = hasNonTerminalJob && !pagesAppendedRef.current && !gaveUp;

  useEffect(() => {
    if (!shouldPoll) {
      return;
    }
    const id = setInterval(() => {
      idleTicksRef.current += 1;
      if (idleTicksRef.current > IDLE_TICKS_BEFORE_GIVING_UP) {
        setGaveUp(true);
        return;
      }
      fetchMore({ variables: { input } }).catch(() => {});
    }, POLL_MS);
    return () => clearInterval(id);
  }, [shouldPoll, fetchMore, input]);

  const hasMore = data?.workflowBatchJobs?.hasMore ?? false;

  const loadMore = useCallback(() => {
    const last = jobs.at(-1);
    if (!(hasMore && last) || inFlightRef.current) {
      return;
    }
    inFlightRef.current = true;
    pagesAppendedRef.current = true;
    fetchMore({
      variables: { input: { ...input, after: last.cursor } },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult?.workflowBatchJobs) {
          return prev;
        }
        const prevJobs = prev.workflowBatchJobs?.jobs ?? [];
        const newJobs = fetchMoreResult.workflowBatchJobs.jobs;
        const prevIds = new Set(prevJobs.map(j => j.id));
        const uniqueNewJobs = newJobs.filter(j => !prevIds.has(j.id));
        return {
          workflowBatchJobs: {
            ...fetchMoreResult.workflowBatchJobs,
            jobs: [...prevJobs, ...uniqueNewJobs]
          }
        };
      }
    }).finally(() => {
      inFlightRef.current = false;
    });
  }, [jobs, hasMore, fetchMore, input]);

  // Exposed so a caller that CREATES a job (retry-all-failed) can surface it
  // immediately: the poll only wakes for a job already on the page, so a brand
  // new one would otherwise not appear until the next filter change.
  const refresh = useCallback(() => {
    pagesAppendedRef.current = false;
    idleTicksRef.current = 0;
    setGaveUp(false);
    refetch().catch(() => {});
  }, [refetch]);

  return { jobs, loading, hasMore, loadMore, refresh };
}
